import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import SaveIcon from "@mui/icons-material/Save";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TitleIcon from "@mui/icons-material/Title";
import SubjectIcon from "@mui/icons-material/Subject";
import ImageIcon from "@mui/icons-material/Image";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TableChartIcon from "@mui/icons-material/TableChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import GridOnIcon from "@mui/icons-material/GridOn";
import CloudIcon from "@mui/icons-material/Cloud";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useClientScope } from "../../../../../shared/contexts";
import { useClientBranding } from "../../../../../shared/hooks";
import {
  useReport,
  useUpdateReport,
  useNarrateReport,
  useExportPdf,
  useExportPptx,
  useGenerateNarrative,
  useGenerateActionPlan,
} from "../hooks/useReporting";
import { reportingApi } from "../api/reporting";
import { ReportCanvas } from "../components/ReportCanvas";
import { LogoUploadButton } from "../components/LogoUploadButton";
import { PresentationMode } from "../components/PresentationMode";
import { ROUTES } from "../../../../../shared/utils";
import type {
  ReportBlock,
  ChartBlock,
  HeadingBlock,
  NarrativeBlock,
  PageBreakBlock,
  ReportFilters,
  CanvasSlideBlock,
} from "../api/types";

// Lightweight unique id for new blocks (crypto.randomUUID is available in all
// supported browsers; falls back to a timestamp+counter if absent).
let blockCounter = 0;
const newId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  blockCounter += 1;
  return `block-${Date.now()}-${blockCounter}`;
};

const SUMMARY_METRICS = [
  "sleep_performance",
  "sleep_duration",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];
const BREAKDOWN_METRICS = [
  "sleep_score",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];

/**
 * Build a fresh block. Data blocks are seeded with the report's current filters
 * (period/audience) so a block added by hand behaves like a generated one; when
 * no filters are known (blank report) they fall back to the client scope only.
 */
const makeBlock = (
  type: ReportBlock["type"],
  filters: ReportFilters,
): ReportBlock => {
  const id = newId();
  switch (type) {
    case "chart":
      return {
        id,
        type: "chart",
        title: "",
        chartKind: "line",
        metric: "recovery_score",
        granularity: "daily",
        windowDays: 90,
      } as ChartBlock;
    case "heading":
      return { id, type: "heading", text: "" } as HeadingBlock;
    case "narrative":
      return { id, type: "narrative", text: "" } as NarrativeBlock;
    case "pageBreak":
      return { id, type: "pageBreak" } as PageBreakBlock;
    case "cover":
      return { id, type: "cover", title: "Section title", subtitle: "" };
    case "execSummary":
      return {
        id,
        type: "execSummary",
        title: "Executive Summary",
        metrics: SUMMARY_METRICS,
        filters,
      };
    case "breakdown":
      return {
        id,
        type: "breakdown",
        title: "Breakdown",
        dimension: "gender",
        metrics: BREAKDOWN_METRICS,
        filters,
      };
    case "kpiCards":
      return {
        id,
        type: "kpiCards",
        title: "Key Metrics",
        metrics: BREAKDOWN_METRICS,
        filters,
      };
    case "aiPanel":
      return {
        id,
        type: "aiPanel",
        title: "AI-Powered Wellness",
        filters,
        show: {
          score: true,
          highlights: true,
          recommendations: true,
          areasToWatch: true,
        },
      };
    case "actionPlan":
      return { id, type: "actionPlan", title: "Action Plan", items: [] };
    case "correlation":
      return {
        id,
        type: "correlation",
        title: "Metric Correlations",
        metrics: BREAKDOWN_METRICS,
        dimension: "department",
        filters,
      };
    case "scatter":
      return {
        id,
        type: "scatter",
        title: "Sleep vs Recovery",
        xMetric: "sleep_score",
        yMetric: "recovery_score",
        dimension: "department",
        filters,
      };
    case "weatherImpact":
      return {
        id,
        type: "weatherImpact",
        title: "Weather Impact",
        metric: "recovery_score",
        filters,
      };
    case "comparison":
      return {
        id,
        type: "comparison",
        title: "Metric Comparison",
        metricA: "recovery_score",
        metricB: "sleep_performance",
        viewMode: "trend",
        segmentBy: "department",
        filters,
      };
    case "image":
      return { id, type: "image", url: "", caption: "", fit: "contain" };
    case "columns":
      return { id, type: "columns", left: null, right: null };
    case "segmentComparison":
      return {
        id,
        type: "segmentComparison",
        title: "Segment Comparison",
        metric: "recovery_score",
        chartKind: "line" as const,
        granularity: "daily" as const,
        filters,
        segments: [
          { label: "Segment A", filterOverride: {} },
          { label: "Segment B", filterOverride: {} },
        ],
      };
    case "canvasSlide":
      return {
        id,
        type: "canvasSlide",
        title: "Canvas slide",
        elements: [],
      } as CanvasSlideBlock;
    default:
      return { id, type: "narrative", text: "" } as NarrativeBlock;
  }
};

export const ReportEditor: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { effectiveClientId } = useClientScope();

  const { data: report, isLoading, isError } = useReport(reportId);
  // Render blocks against the client the report is scoped to (falls back to the
  // live scope for legacy/blank reports without a client_id).
  const reportClientId = report?.client_id ?? effectiveClientId;
  const { data: brandingData } = useClientBranding(reportClientId);
  const clientLogoUrl =
    brandingData?.data?.dark_logo_url ||
    brandingData?.data?.logo_dark_url ||
    brandingData?.data?.logo_url ||
    null;
  const updateReport = useUpdateReport(reportId as string);
  const narrate = useNarrateReport(reportId as string);
  const exportPdf = useExportPdf(reportId as string);
  const exportPptx = useExportPptx(reportId as string);
  const generateNarrative = useGenerateNarrative(reportId as string);
  const generateActionPlan = useGenerateActionPlan(reportId as string);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [blocksHistory, setBlocksHistory] = useState<ReportBlock[][]>([]);
  const [dirty, setDirty] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [presenting, setPresenting] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [segments, setSegments] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // Seed local state once the report loads.
  useEffect(() => {
    if (report) {
      setTitle(report.title);
      setDescription(report.description ?? "");
      setBlocks(report.layout?.blocks ?? []);
      setBlocksHistory([]);
      setDirty(false);
    }
  }, [report]);

  // Ctrl+Z undo — skip when focus is inside a text input to avoid fighting the browser.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // If narration already exists, fetch a fresh presigned URL for playback.
  useEffect(() => {
    if (report?.audio_status === "ready" && reportId) {
      reportingApi
        .getAudio(reportId)
        .then((r) => {
          setAudioUrl(r.audio_url);
          setSegments(r.segments ?? []);
        })
        .catch(() => {});
    }
  }, [report?.audio_status, reportId]);

  const mutate = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setDirty(true);
  };

  const setBlocksWithHistory = (next: ReportBlock[]) => {
    setBlocksHistory((h) => [...h.slice(-49), blocks]);
    setBlocks(next);
    setDirty(true);
  };

  const handleUndo = () => {
    if (blocksHistory.length === 0) return;
    const prev = blocksHistory[blocksHistory.length - 1];
    setBlocksHistory((h) => h.slice(0, -1));
    setBlocks(prev);
    setDirty(true);
  };

  // Inherit period/audience from an existing data block so hand-added blocks
  // match the rest of the report; empty for a truly blank report.
  const inheritedFilters = (): ReportFilters => {
    const withFilters = blocks.find(
      (b) => "filters" in b && b.filters,
    ) as { filters?: ReportFilters } | undefined;
    return withFilters?.filters ?? {};
  };

  const handleAdd = (type: ReportBlock["type"]) => {
    const newBlock = makeBlock(type, inheritedFilters());
    if (insertIndex !== null) {
      const next = [...blocks];
      next.splice(insertIndex, 0, newBlock);
      setBlocksWithHistory(next);
      setInsertIndex(null);
    } else {
      setBlocksWithHistory([...blocks, newBlock]);
    }
    setAddMenuAnchor(null);
  };

  const handleSave = async () => {
    await updateReport.mutateAsync({
      title: title.trim() || "Untitled report",
      description,
      layout: { blocks },
    });
    setDirty(false);
    setToast("Report saved");
  };

  // AI narrative generation reads the report's saved layout server-side, so we
  // persist the current edits first, then ask for Director-targeted copy.
  const handleGenerateNarrative = async (hint?: string): Promise<string> => {
    if (dirty) await handleSave();
    const result = await generateNarrative.mutateAsync(hint);
    return result.text;
  };

  // Action-plan generation also reads the saved layout server-side, so persist
  // first, then return the generated items for the block to store.
  const handleGenerateActionPlan = async (): Promise<string[]> => {
    if (dirty) await handleSave();
    const result = await generateActionPlan.mutateAsync(undefined);
    return result.items;
  };

  const handleNarrate = async () => {
    // Persist first so narration reflects the current layout.
    if (dirty) await handleSave();
    const result = await narrate.mutateAsync();
    setAudioUrl(result.audio_url);
    setSegments(result.segments ?? []);
    setToast(
      result.mocked
        ? "Narration generated (mock voice — set ELEVENLABS_API_KEY for real audio)"
        : "Narration generated",
    );
  };

  const handleExportPdf = async () => {
    if (dirty) await handleSave();
    const result = await exportPdf.mutateAsync();
    window.open(result.pdf_url, "_blank", "noopener");
    setToast("PDF pack ready");
  };

  const handleExportPptx = async () => {
    if (dirty) await handleSave();
    const result = await exportPptx.mutateAsync();
    window.open(result.pptx_url, "_blank", "noopener");
    setToast("PowerPoint ready");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Couldn't load this report.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTES.REPORTING)}
          size="small"
          color="inherit"
        >
          All reports
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Tooltip title="Undo last block change (Ctrl+Z)">
            <span>
              <Button
                variant="outlined"
                startIcon={<UndoIcon />}
                onClick={handleUndo}
                disabled={blocksHistory.length === 0}
              >
                Undo
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<SlideshowIcon />}
            onClick={() => setPresenting(true)}
            disabled={blocks.length === 0}
          >
            Present
          </Button>
          <Button
            variant="outlined"
            startIcon={
              narrate.isPending ? <CircularProgress size={16} /> : <RecordVoiceOverIcon />
            }
            onClick={handleNarrate}
            disabled={narrate.isPending || blocks.length === 0}
          >
            Narrate
          </Button>
          <Button
            variant="outlined"
            startIcon={
              exportPdf.isPending ? <CircularProgress size={16} /> : <PictureAsPdfIcon />
            }
            onClick={handleExportPdf}
            disabled={exportPdf.isPending || blocks.length === 0}
          >
            PDF pack
          </Button>
          <Button
            variant="outlined"
            startIcon={
              exportPptx.isPending ? <CircularProgress size={16} /> : <SlideshowIcon />
            }
            onClick={handleExportPptx}
            disabled={exportPptx.isPending || blocks.length === 0}
          >
            PowerPoint
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={updateReport.isPending || !dirty}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      <TextField
        fullWidth
        variant="standard"
        placeholder="Report title"
        value={title}
        onChange={(e) => mutate(setTitle)(e.target.value)}
        slotProps={{ input: { style: { fontSize: 30, fontWeight: 700 } } }}
        sx={{ mb: 1 }}
      />
      <TextField
        fullWidth
        variant="standard"
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => mutate(setDescription)(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box
        sx={{
          mb: 3,
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Slide branding — PEAK4 mark shown on every slide; client logo (top-right) is
          used by default when set against the client.
        </Typography>
        <LogoUploadButton clientId={reportClientId} currentLogoUrl={clientLogoUrl} />
      </Box>

      {narrate.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Narration failed. Try again.
        </Alert>
      )}
      {exportPdf.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          PDF export failed. Try again.
        </Alert>
      )}

      <ReportCanvas
        blocks={blocks}
        clientId={reportClientId}
        onChange={setBlocksWithHistory}
        onGenerateNarrative={handleGenerateNarrative}
        onGenerateActionPlan={handleGenerateActionPlan}
        onInsertAt={(index, e) => {
          setInsertIndex(index);
          setAddMenuAnchor(e.currentTarget as HTMLElement);
        }}
      />

      <Menu
        anchorEl={addMenuAnchor}
        open={!!addMenuAnchor}
        onClose={() => { setAddMenuAnchor(null); setInsertIndex(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            p: 2,
            width: 700,
            maxWidth: "95vw",
            maxHeight: "80vh",
            overflowY: "auto",
          },
        }}
      >
        <Stack spacing={2}>
          {[
            {
              label: "Layout",
              color: "#7c4dff",
              items: [
                { type: "cover", label: "Cover", desc: "Section title slide", icon: <BrokenImageIcon fontSize="small" /> },
                { type: "heading", label: "Heading", desc: "Section heading text", icon: <TitleIcon fontSize="small" /> },
                { type: "narrative", label: "Narrative", desc: "Written commentary", icon: <SubjectIcon fontSize="small" /> },
                { type: "image", label: "Image", desc: "Upload or paste URL", icon: <ImageIcon fontSize="small" /> },
                { type: "columns", label: "Two columns", desc: "Side-by-side blocks", icon: <ViewColumnIcon fontSize="small" /> },
                { type: "pageBreak", label: "Page break", desc: "New slide boundary", icon: <HorizontalRuleIcon fontSize="small" /> },
                { type: "canvasSlide", label: "Canvas", desc: "Free-form layout", icon: <DashboardCustomizeIcon fontSize="small" /> },
              ],
            },
            {
              label: "Charts & Data",
              color: "#00b8d9",
              items: [
                { type: "chart", label: "Chart", desc: "Line, bar or donut", icon: <ShowChartIcon fontSize="small" /> },
                { type: "kpiCards", label: "KPI cards", desc: "Big number stat tiles", icon: <DashboardIcon fontSize="small" /> },
                { type: "execSummary", label: "Exec summary", desc: "RAG table this vs last", icon: <TableChartIcon fontSize="small" /> },
                { type: "breakdown", label: "Breakdown", desc: "By dept / age / shift", icon: <BarChartIcon fontSize="small" /> },
                { type: "comparison", label: "Metric comparison", desc: "Two metrics over time", icon: <CompareArrowsIcon fontSize="small" /> },
                { type: "segmentComparison", label: "Segment comparison", desc: "Groups on same chart", icon: <GroupWorkIcon fontSize="small" /> },
                { type: "correlation", label: "Correlation matrix", desc: "Pairwise metric heat map", icon: <GridOnIcon fontSize="small" /> },
                { type: "scatter", label: "Scatter", desc: "Two metrics by segment", icon: <ScatterPlotIcon fontSize="small" /> },
                { type: "weatherImpact", label: "Weather impact", desc: "Wellbeing vs weather", icon: <CloudIcon fontSize="small" /> },
              ],
            },
            {
              label: "AI",
              color: "#ff6d00",
              items: [
                { type: "aiPanel", label: "AI insights", desc: "Highlights & recommendations", icon: <AutoAwesomeIcon fontSize="small" /> },
                { type: "actionPlan", label: "Action plan", desc: "AI-generated next steps", icon: <FormatListBulletedIcon fontSize="small" /> },
              ],
            },
          ].map((group) => (
            <Box key={group.label}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, textTransform: "uppercase", color: group.color, display: "block", mb: 1, letterSpacing: 1 }}
              >
                {group.label}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {group.items.map(({ type, label, desc, icon }) => (
                  <Box
                    key={type}
                    onClick={() => handleAdd(type as ReportBlock["type"])}
                    sx={{
                      width: 118,
                      p: 1,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.75,
                      transition: "all 0.15s",
                      "&:hover": {
                        bgcolor: alpha(group.color, 0.08),
                        borderColor: group.color,
                        "& .block-icon": { color: group.color },
                      },
                    }}
                  >
                    <Box className="block-icon" sx={{ color: "text.disabled", mt: 0.15, flexShrink: 0, transition: "color 0.15s", "& svg": { fontSize: 16 } }}>
                      {icon}
                    </Box>
                    <Box>
                      <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, display: "block" }}>{label}</Typography>
                      <Typography sx={{ fontSize: "0.65rem", lineHeight: 1.2, color: "text.disabled" }}>{desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Menu>

      {dirty && (
        <Chip
          label="Unsaved changes"
          color="warning"
          size="small"
          sx={{ position: "fixed", bottom: 16, right: 16 }}
        />
      )}

      <PresentationMode
        open={presenting}
        onClose={() => setPresenting(false)}
        blocks={blocks}
        clientId={reportClientId}
        audioUrl={audioUrl}
        segments={segments}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};
